<template>
  <section
    class="relative w-screen -mx-[calc(50vw-50%)] bg-zinc-50 text-zinc-900 dark:bg-black dark:text-zinc-50 overflow-hidden">
    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute inset-0 transition-colors duration-500
               bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.12),transparent_60%)]
               dark:bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.18),transparent_60%)]"></div>
    </div>
    <div class="mx-auto max-w-[1300px] px-4 sm:px-6 lg:px-10">
      <article v-for="(s, i) in sections" :key="s.id" :id="s.id"
        class="grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] gap-10 lg:gap-16 py-20 sm:py-24 lg:py-32">
        <div class="flex items-center">
          <div class="w-full">
            <h2
              class="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-semibold leading-[1.02] sm:leading-[0.98] tracking-tight">
              <span :class="[
                'block transition-all duration-700',
                active === i ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              ]">
                {{ s.t1 }}
              </span>
              <span :class="[
                'block transition-all duration-700 delay-100',
                active === i ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              ]">
                {{ s.t2 }}
              </span>
            </h2>

            <p :class="[
              'mt-4 sm:mt-6 max-w-md text-zinc-600 dark:text-zinc-300 text-[14px] sm:text-[15px] leading-relaxed transition-all duration-700 delay-300',
              active === i ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
            ]">
              {{ s.d }}
            </p>

            <ul
              class="mt-8 sm:mt-10 divide-y divide-black/10 dark:divide-white/10 rounded-xl overflow-hidden ring-1 ring-black/10 dark:ring-white/10">
              <li v-for="(it, j) in s.items" :key="j"
                class="flex items-center justify-between px-3 sm:px-4 py-3 sm:py-4 hover:bg-black/[0.03] dark:hover:bg-white/[0.03]">
                <span class="text-[13px] sm:text-sm text-zinc-800 dark:text-zinc-200">
                  {{ it }}
                </span>
                <span class="material-symbols-outlined text-zinc-500 dark:text-zinc-400 text-base sm:text-lg">
                  chevron_right
                </span>
              </li>
            </ul>

            <div class="mt-8 sm:mt-10 h-px bg-black/10 dark:bg-white/10"></div>
          </div>
        </div>

        <div class="flex items-center">
          <div :class="[
            'w-full transition duration-700',
            active === i ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
          ]">
            <div
              class="relative overflow-hidden rounded-2xl w-full md:max-w-[980px] ring-1 ring-black/10 dark:ring-white/10 bg-[#0D0D13]">
              <div v-if="i === 0" class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 p-5 sm:p-8 text-white">
                <div v-for="p in palettes" :key="p.name"
                  class="rounded-xl bg-[#15151D] ring-1 ring-white/10 p-4 sm:p-5 space-y-3">
                  <div class="flex justify-between text-[11px] sm:text-xs">
                    <span class="font-medium">{{ p.name }}</span>
                    <span
                      class="px-2 py-0.5 rounded-full bg-white/10 text-[9px] sm:text-[10px] uppercase tracking-wider">
                      {{ p.tag }}
                    </span>
                  </div>
                  <div class="flex gap-2">
                    <div v-for="c in p.colors" :key="c" class="flex-1 h-10 sm:h-12 rounded-lg border border-white/10"
                      :style="{ backgroundColor: c }"></div>
                  </div>
                  <div class="text-[10px] sm:text-[11px] text-zinc-400 font-mono truncate">
                    {{ p.colors.join(' • ') }}
                  </div>
                </div>
              </div>

              <div v-else-if="i === 1" class="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 p-5 sm:p-8 text-white">
                <div v-for="p in accents" :key="p.name"
                  class="rounded-xl bg-[#15151D] ring-1 ring-white/10 p-4 sm:p-5 space-y-3">
                  <div class="flex justify-between text-[11px] sm:text-xs">
                    <span class="font-medium">{{ p.name }}</span>
                    <span class="text-[9px] sm:text-[10px] uppercase text-zinc-400 tracking-wider">
                      Accents
                    </span>
                  </div>
                  <div class="flex gap-2">
                    <div v-for="c in p.colors" :key="c" class="flex-1 h-8 sm:h-10 rounded-md border border-white/10"
                      :style="{ backgroundColor: c }"></div>
                  </div>
                  <div class="text-[10px] sm:text-[11px] text-zinc-400 font-mono truncate">
                    {{ p.colors.join(' • ') }}
                  </div>
                </div>
              </div>

              <div v-else-if="i === 2" class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 p-5 sm:p-8 text-white">
                <div v-for="pair in surfaces" :key="pair.name"
                  class="rounded-xl bg-[#15151D] ring-1 ring-white/10 p-4 sm:p-5 space-y-4">
                  <div class="flex justify-between text-[11px] sm:text-xs">
                    <span class="font-medium">{{ pair.name }}</span>
                    <span class="uppercase text-[9px] sm:text-[10px] text-zinc-400 tracking-wider">
                      Light / Dark
                    </span>
                  </div>
                  <div class="grid grid-cols-2 gap-3 sm:gap-4">
                    <div class="rounded-lg border border-white/10 p-2.5 sm:p-3 space-y-2">
                      <div class="h-7 sm:h-8 rounded-md" :style="{ backgroundColor: pair.light.bg }"></div>
                      <div class="flex gap-2">
                        <div class="flex-1 h-4 sm:h-5 rounded-md" :style="{ backgroundColor: pair.light.primary }">
                        </div>
                        <div class="flex-1 h-4 sm:h-5 rounded-md" :style="{ backgroundColor: pair.light.accent }"></div>
                      </div>
                    </div>
                    <div class="rounded-lg border border-white/10 p-2.5 sm:p-3 space-y-2">
                      <div class="h-7 sm:h-8 rounded-md" :style="{ backgroundColor: pair.dark.bg }"></div>
                      <div class="flex gap-2">
                        <div class="flex-1 h-4 sm:h-5 rounded-md" :style="{ backgroundColor: pair.dark.primary }"></div>
                        <div class="flex-1 h-4 sm:h-5 rounded-md" :style="{ backgroundColor: pair.dark.accent }"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="p-5 sm:p-8 text-white">
                <div class="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                  <div v-for="t in tokens" :key="t.token"
                    class="rounded-xl bg-[#15151D] ring-1 ring-white/10 p-4 sm:p-5 space-y-3">
                    <div class="flex justify-between text-[11px] sm:text-xs">
                      <span class="font-medium">{{ t.name }}</span>
                      <span class="font-mono text-[9px] sm:text-[10px] text-zinc-400">
                        {{ t.token }}
                      </span>
                    </div>
                    <div class="h-8 sm:h-10 rounded-md border border-white/10" :style="{ backgroundColor: t.value }">
                    </div>
                    <div class="text-[10px] sm:text-[11px] text-zinc-400 font-mono truncate">
                      {{ t.value }} • {{ t.role }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <nav class="hidden lg:flex fixed left-6 xl:left-8 top-1/2 -translate-y-1/2 z-20">
        <ul class="space-y-3">
          <li v-for="(s, i) in sections" :key="i">
            <button @click="scrollToIndex(i)" :class="[
              'w-2.5 h-2.5 rounded-full transition',
              active === i
                ? 'bg-zinc-800 dark:bg-white'
                : 'bg-black/30 dark:bg-white/30 hover:bg-black/60 dark:hover:bg-white/60'
            ]" />
          </li>
        </ul>
      </nav>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watchEffect } from 'vue'

const isDarkMode = ref(true)

watchEffect(() => {
  const r = document.documentElement
  if (isDarkMode.value) r.classList.add('dark')
  else r.classList.remove('dark')
})

const sections = [
  {
    id: 's1',
    t1: 'Palette Studio',
    t2: 'Vibrant color systems',
    d: 'High-impact color palettes prepared for modern digital products.',
    items: ['Browse palettes', 'Copy hex codes', 'Save to workspace', 'Preview on UI']
  },
  {
    id: 's2',
    t1: 'Accent Sets',
    t2: 'Brand-ready tones',
    d: 'CTA, status and marketing accents crafted to stand out without clashing.',
    items: ['CTA colors', 'Status palettes', 'Marketing accents', 'Info tones']
  },
  {
    id: 's3',
    t1: 'Surface Systems',
    t2: 'Light and dark pairs',
    d: 'Paired backgrounds that keep content readable across themes.',
    items: ['Theme pairing', 'Panel previews', 'Accessible contrast', 'Dynamic themes']
  },
  {
    id: 's4',
    t1: 'Color Tokens',
    t2: 'Design-system ready',
    d: 'Semantic color tokens that plug directly into code and design tools.',
    items: ['Token naming', 'Export JSON', 'CSS variables', 'Design tool sync']
  }
]

const palettes = [
  { name: 'Aurora UI', tag: 'Interface', colors: ['#00E0FF', '#0078FF', '#00D27A', '#F9F871'] },
  { name: 'Neon Pulse', tag: 'SaaS', colors: ['#FF2E63', '#FF7A00', '#FFE45E', '#21E6C1'] },
  { name: 'Midnight Air', tag: 'Dashboards', colors: ['#0F172A', '#1D2A3F', '#38BDF8', '#22C55E'] },
  { name: 'Candy Pop', tag: 'Marketing', colors: ['#FF4B91', '#FF9A62', '#FFD447', '#7CF5FF'] }
]

const accents = [
  { name: 'Primary CTA', colors: ['#00E29B', '#00C27F', '#009761'] },
  { name: 'Warm CTA', colors: ['#FFB224', '#FF9F1C', '#FF7A00'] },
  { name: 'Danger', colors: ['#FF4757', '#E11D48', '#9F1239'] },
  { name: 'Info', colors: ['#38BDF8', '#0EA5E9', '#0369A1'] },
  { name: 'Highlight', colors: ['#F97316', '#FB7185', '#A855F7'] },
  { name: 'Muted', colors: ['#E5E7EB', '#CBD5F5', '#9CA3AF'] }
]

const surfaces = [
  {
    name: 'Dashboard Shell',
    light: { bg: '#F7F8FA', primary: '#111827', accent: '#2563EB' },
    dark: { bg: '#020617', primary: '#E5E7EB', accent: '#38BDF8' }
  },
  {
    name: 'Marketing Hero',
    light: { bg: '#EEF2FF', primary: '#0F172A', accent: '#6D28D9' },
    dark: { bg: '#02011A', primary: '#F9FAFB', accent: '#A855F7' }
  }
]

const tokens = [
  { name: 'Primary', token: 'color.primary', value: '#111827', role: 'Main text' },
  { name: 'Accent', token: 'color.accent', value: '#0EA5E9', role: 'Links and CTAs' },
  { name: 'Success', token: 'color.success', value: '#22C55E', role: 'Positive states' },
  { name: 'Warning', token: 'color.warning', value: '#FACC15', role: 'Warnings and banners' },
  { name: 'Error', token: 'color.error', value: '#EF4444', role: 'Destructive actions' },
  { name: 'Surface', token: 'surface.base', value: '#0D0D13', role: 'App background' }
]

const active = ref(0)

function updateActiveIndex() {
  const arts = document.querySelectorAll('article[id^="s"]')
  const mid = window.innerHeight * 0.5
  let nearest = 0
  let best = Infinity
  arts.forEach((el, i) => {
    const r = el.getBoundingClientRect()
    const d = Math.abs(r.top + r.height * 0.5 - mid)
    if (d < best) {
      best = d
      nearest = i
    }
  })
  active.value = nearest
}

function scrollToIndex(i) {
  const els = document.querySelectorAll('article[id^="s"]')
  const el = els[i]
  if (!el) return
  const top = window.scrollY + el.getBoundingClientRect().top - window.innerHeight * 0.08
  window.scrollTo({ top, behavior: 'smooth' })
}

let observer

onMounted(async () => {
  await nextTick()
  const arts = document.querySelectorAll('article[id^="s"]')
  observer = new IntersectionObserver(() => updateActiveIndex(), { threshold: 0.4 })
  arts.forEach(el => observer.observe(el))
  window.addEventListener('scroll', updateActiveIndex, { passive: true })
  updateActiveIndex()
})

onBeforeUnmount(() => {
  if (observer) observer.disconnect()
  window.removeEventListener('scroll', updateActiveIndex)
})
</script>
